import type { ReactionInstance } from './ReactionInstance';
import type { ReactionMonitoring } from './reactionMonitoring';

// Сохраняем ссылку на нативный setTimeout до подмены фейковыми таймерами в тестах
const nativeSetTimeout = globalThis.setTimeout;

// Глобальный реестр всех запущенных реакций
const runningReactions = new Set<Promise<unknown>>();

// Глобальный реестр всех экземпляров реакций
const allReactionsInstances: Set<Map<unknown, ReactionInstance<unknown, unknown>>> = new Set();

// Глобальный реестр мониторингов реакций
const allReactionsMonitorings: Set<ReactionMonitoring> = new Set();

// Отслеживание, ожидаются ли реакции (для тестирования)
let isReactionsWaited: boolean = false;

/**
 * Добавить промис реакции в реестр запущенных реакций
 */
export function addRunningReactionToRegistry(reactionPromise: Promise<void>): Promise<void> {
    runningReactions.add(reactionPromise);
    return reactionPromise.finally(() => {
        runningReactions.delete(reactionPromise);
    });
}

/**
 * Дождаться завершения всех запущенных реакций (с ограничением рекурсии)
 */
export async function waitAllRunningReactions(limit = 5): Promise<void> {
    isReactionsWaited = true;

    // Прокручиваем несколько макротасков, чтобы потенциальные отложенные таймеры отработали и реакции запустились.
    // Используем nativeSetTimeout, чтобы не зависеть от jest.useFakeTimers()
    for (let i = 0; i < 10; i++) {
        await new Promise<void>(resolve => nativeSetTimeout(resolve, 0));
    }

    await Promise.allSettled(runningReactions);

    if (limit >= 0 && runningReactions.size > 0) {
        await waitAllRunningReactions(limit - 1);
    }
}

function getAndResetIsReactionsWaited(): boolean {
    if (runningReactions.size > 0) {
        const result = isReactionsWaited;
        isReactionsWaited = false;
        return result;
    }

    isReactionsWaited = false;
    return true;
}

/**
 * Проверить, что все реакции были ожидаемы (утилита для тестирования)
 */
export function assertIsReactionsWaited(): void {
    const message =
        // eslint-disable-next-line @adv-frontend/config/no-rus-string-locales-rule -- сообщение об ошибке только для тестов
        'В тесте присутствуют запущенные реакции. Добавьте ожидание выполнения реакций "waitAllRunningReactions", чтобы исключить нестабильность теста.';
    if (!getAndResetIsReactionsWaited()) {
        throw new Error(message);
    }
}

/**
 * Очистить все реакции - останавливает подписки и удаляет экземпляры
 */
export function cleanupReactions(): void {
    for (const reactionInstances of allReactionsInstances) {
        for (const reactionInstance of reactionInstances.values()) {
            reactionInstance.destroy();
            reactionInstance.cancelActiveRuns();
            reactionInstances.delete(reactionInstance.param);
        }
    }

    // Сбрасываем счетчики мониторинга, иначе они копятся между тестами и ловят лимит на границе никак не связанных друг с другом тестов
    for (const reactionMonitoring of allReactionsMonitorings) {
        reactionMonitoring.reset();
    }
}

/**
 * Зарегистрировать карту экземпляров в глобальном реестре
 */
export function registerInstancesMap<R, P>(instances: Map<P, ReactionInstance<R, P>>): void {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип параметров не важен в кеше
    allReactionsInstances.add(instances as Map<unknown, ReactionInstance<unknown, unknown>>);
}

/**
 * Зарегистрировать мониторинг реакции в глобальном реестре
 */
export function registerReactionMonitoring(monitoring: ReactionMonitoring): void {
    allReactionsMonitorings.add(monitoring);
}
